import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'

pdfMake.addVirtualFileSystem(pdfFonts)

const generateReceiptPDF = (
  withdrawnItems = [],
  customerName = '',
  pedidoNumber = '',
  empresa = {},
) => {
  // Paleta
  const primary = '#1F6FEB'
  const primaryDark = '#184db1'
  const secondary = '#111827'
  const muted = '#6B7280'
  const border = '#E5E7EB'
  const stripe = '#F9FAFB'
  const cardBg = '#F5F7FB' // bloco sutil para o cliente

  const now = new Date()
  const dataFmt = format(now, "dd/MM/yyyy HH:mm:ss", { locale: ptBR })
  const receiptId = `REC-${pedidoNumber || '0000'}-${now.getTime()}`
  const qrPayload = JSON.stringify({
    recibo: receiptId,
    pedido: pedidoNumber,
    cliente: customerName,
    geradoEm: format(now, "yyyy-MM-dd'T'HH:mm:ssXXX"),
  })

  // ===== EMPRESA no topo =====
  const empresaHeader = {
    stack: [
      { text: empresa?.EMP_RAZAO_SOCIAL || 'Nome da Empresa', fontSize: 16, bold: true, color: secondary, margin: [0, 0, 0, 4] },
      {
        text: [
          empresa?.EMP_ENDERECO,
          empresa?.EMP_NUMERO,
          empresa?.EMP_BAIRRO,
          `${empresa?.EMP_CIDADE || ''}-${empresa?.EMP_ESTADO || ''}`,
          empresa?.EMP_CEP ? `CEP ${empresa.EMP_CEP}` : null,
        ].filter(Boolean).join(', '),
        fontSize: 10, color: muted, margin: [0, 0, 0, 2],
      },
      empresa?.EMP_TELEFONE ? { text: `Telefone: ${empresa.EMP_TELEFONE}`, fontSize: 10, color: muted } : null,
      empresa?.EMP_CNPJ ? { text: `CNPJ: ${empresa.EMP_CNPJ}`, fontSize: 10, color: muted } : null,
    ].filter(Boolean),
    alignment: 'center',
    margin: [0, 0, 0, 18],
  }

  // ===== Título + ID do recibo (QR) + frase =====
  const tituloRecibo = {
    margin: [0, 0, 0, 14],
    table: {
      widths: ['*', 'auto'],
      body: [[
        {
          stack: [
            { text: 'Comprovante de Retirada de Itens', fontSize: 14, bold: true, color: secondary, margin: [0, 0, 0, 6] },
            {
              table: { body: [[{ text: `ID do Recibo: ${receiptId}`, color: 'white', bold: true, fontSize: 10, margin: [8, 4, 8, 4] }]] },
              layout: {
                fillColor: () => primary,
                paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
                hLineWidth: () => 0, vLineWidth: () => 0,
              },
              width: 'auto',
              margin: [0, 0, 0, 4],
            },
            { text: 'Este documento comprova a retirada dos itens listados abaixo para o pedido informado.', fontSize: 10, color: muted, margin: [0, 6, 0, 0] },
          ],
        },
        { qr: qrPayload, fit: 70, foreground: primaryDark, alignment: 'right' },
      ]],
    },
    layout: 'noBorders',
  }

  // ===== BLOCO CLIENTE/PEDIDO — minimalista =====
  // • Cliente grande, com label pequena em cima
  // • Pedido em formato "chip" à direita
  // • Data centralizada, discreta
  const clientePedidoBlock = {
    margin: [0, 0, 0, 16],
    table: {
      widths: ['*', 'auto'],
      body: [
        [
          {
            stack: [
              { text: 'CLIENTE', color: muted, fontSize: 8, letterSpacing: 1, margin: [0, 0, 0, 2] },
              { text: customerName || '—', fontSize: 12, bold: true, color: secondary },
            ],
          },
          {
            table: {
              body: [[{ text: `PEDIDO ${pedidoNumber || '—'}`, color: 'white', bold: true, fontSize: 10, margin: [10, 5, 10, 5] }]],
            },
            layout: {
              fillColor: () => secondary,
              hLineWidth: () => 0, vLineWidth: () => 0,
              paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
            },
            alignment: 'right',
          },
        ],
        [
          { text: `Data: ${dataFmt}`, color: muted, fontSize: 9, alignment: 'center', colSpan: 2, margin: [0, 6, 0, 0] },
          {},
        ],
      ],
    },
    layout: {
      fillColor: (rowIndex) => cardBg, // fundo suave tipo "card"
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 12,
      paddingRight: () => 12,
      paddingTop: (i) => (i === 0 ? 12 : 10),
      paddingBottom: (i) => (i === 0 ? 10 : 12),
    },
  }

  // ===== Tabela de itens =====
  const headerRow = [
    { text: 'SKU', style: 'th' },
    { text: 'Descrição', style: 'th' },
    { text: 'Quantidade', style: 'th', alignment: 'right' },
  ]
  const itemsBody = withdrawnItems.map((it) => ([
    { text: String(it?.CODIGO ?? ''), style: 'td' },
    { text: String(it?.DESCRICAO ?? ''), style: 'td' },
    { text: Number(it?.QUANTIDADE_RETIRADA || 0).toFixed(2), style: 'td', alignment: 'right' },
  ]))
  const totalQtd = withdrawnItems.reduce((acc, it) => acc + (Number(it?.QUANTIDADE_RETIRADA) || 0), 0)

  const itemsTable = {
    margin: [0, 6, 0, 10],
    table: { headerRows: 1, widths: ['auto', '*', 90], body: [headerRow, ...itemsBody] },
    layout: {
      fillColor: (rowIndex) => rowIndex === 0 ? primary : rowIndex % 2 === 0 ? stripe : null,
      hLineColor: () => border, vLineColor: () => border,
      hLineWidth: (i) => (i <= 1 ? 0 : 0.5), vLineWidth: () => 0.5,
      paddingLeft: () => 10, paddingRight: () => 10,
      paddingTop: (i) => (i === 0 ? 10 : 6), paddingBottom: (i) => (i === 0 ? 10 : 6),
    },
  }

  // ===== Resumo =====
  const resumo = {
    columns: [
      { text: ' ', width: '*' },
      {
        table: {
          widths: ['*', 90],
          body: [
            [{ text: 'Total de Itens', alignment: 'right', color: muted, fontSize: 10 },
             { text: String(withdrawnItems.length || 0), alignment: 'right', fontSize: 11 }],
            [{ text: 'Quantidade Total', alignment: 'right', color: muted, fontSize: 10 },
             { text: totalQtd.toFixed(2), alignment: 'right', fontSize: 12, bold: true }],
          ],
        },
        layout: {
          hLineColor: () => border, vLineColor: () => border,
          hLineWidth: () => 0.5, vLineWidth: () => 0.5,
          paddingLeft: () => 8, paddingRight: () => 8,
          paddingTop: () => 6, paddingBottom: () => 6,
        },
        width: 250,
      },
    ],
    margin: [0, 0, 0, 12],
  }

  // ===== Assinatura =====
  const sign = [
    { text: 'Assinatura do Cliente', style: 'h6', margin: [0, 18, 0, 6] },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 220, y2: 0, lineWidth: 0.8, lineColor: secondary }], margin: [0, 4, 0, 0] },
    { text: customerName || ' ', fontSize: 9, color: muted, margin: [0, 4, 0, 0] },
  ]

  // ===== Documento =====
  const docDefinition = {
    info: {
      title: `Comprovante de Retirada - ${pedidoNumber}`,
      subject: 'Comprovante de Retirada de Itens',
      keywords: 'retirada, comprovante, pedido',
      creator: empresa?.EMP_RAZAO_SOCIAL || 'Sistema',
      producer: 'pdfmake',
    },
    pageSize: 'A4',
    pageMargins: [28, 56, 28, 60],
    defaultStyle: { fontSize: 10, color: secondary },
    styles: {
      h6: { fontSize: 12, bold: true, color: secondary },
      th: { color: 'white', bold: true, fontSize: 10 },
      td: { fontSize: 10, color: secondary },
    },
    header: () => ({
      canvas: [{ type: 'rect', x: 0, y: 0, w: 595.28, h: 2, color: primaryDark }],
      margin: [0, 0, 0, 0],
    }),
    footer: (currentPage, pageCount) => ({
      columns: [
        { text: `Gerado em ${dataFmt}`, color: muted, fontSize: 8 },
        { text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', color: muted, fontSize: 8 },
      ],
      margin: [28, 8, 28, 16],
    }),
    content: [
      empresaHeader,
      tituloRecibo,
      clientePedidoBlock,     // << bloco minimalista aqui
      { text: 'Itens Retirados', style: 'h6', margin: [0, 6, 0, 4] },
      itemsTable,
      resumo,
      ...sign,
    ],
  }

  pdfMake.createPdf(docDefinition).open()
}

export default generateReceiptPDF
